/**
 * Klaviyo star rating widget wrapper.
 *
 * Klaviyo scans for empty `.klaviyo-star-rating-widget` nodes when its bundle boots and
 * never re-scans on its own, so the markup is authored alongside the element (Liquid on
 * the PDP, JSX in SearchSpring cards) and this element only writes it as a fallback.
 * Anything the boot scan misses asks Klaviyo to re-render via
 * `window.klReviewsObject.renderWidgets()`.
 *
 * Klaviyo ships as an async app embed, so neither the moment its bundle arrives nor the
 * moment its rating data lands is predictable, and `renderWidgets()` silently no-ops
 * before either. Requests are therefore pooled and retried on a backoff until every
 * pending widget has actually rendered, instead of being fired once and assumed to work.
 */

export const KLAVIYO_STAR_RATING_READY_EVENT = 'klaviyo-star-rating:ready'

type KlReviewsObject = { renderWidgets?: () => void }

type PendingStarRating = {
  isConnected: boolean
  syncRenderedState: () => boolean
}

const WIDGET_SELECTOR = '.klaviyo-star-rating-widget'
const RENDERED_SELECTOR = '.kl_reviews__star_rating_widget'
const RATING_BUTTON_SELECTOR = 'button.kl_reviews__star_rating_widget'

const DISABLE_INTERACTION_ATTRIBUTE = 'disable-interaction'

const BATCH_DEBOUNCE_MS = 100

const KLAVIYO_POLL_MS = 200
const KLAVIYO_WAIT_MS = 30000

const RENDER_RETRY_MS = [300, 800, 2000, 5000, 5000]

const pending = new Set<PendingStarRating>()

let timer: ReturnType<typeof setTimeout> | null = null
let nextRunAt = 0
let retryIndex = 0
let waitingSince = 0

function scheduleRun(delay: number) {
  const runAt = Date.now() + delay

  if (timer) {
    if (nextRunAt <= runAt) return
    clearTimeout(timer)
  }

  nextRunAt = runAt
  timer = setTimeout(runKlaviyoRender, delay)
}

function requestKlaviyoRender(element: PendingStarRating) {
  pending.add(element)
  retryIndex = 0
  if (!waitingSince) waitingSince = Date.now()
  scheduleRun(BATCH_DEBOUNCE_MS)
}

function stopKlaviyoRender() {
  if (timer) clearTimeout(timer)
  timer = null
  nextRunAt = 0
  retryIndex = 0
  waitingSince = 0
  // Elements keep their own observer, so a very late Klaviyo render still gets picked up.
  pending.clear()
}

function runKlaviyoRender() {
  timer = null
  nextRunAt = 0

  pending.forEach((element) => {
    if (!element.isConnected || element.syncRenderedState()) pending.delete(element)
  })

  if (!pending.size) {
    stopKlaviyoRender()
    return
  }

  const klReviews = (window as unknown as { klReviewsObject?: KlReviewsObject }).klReviewsObject

  if (!klReviews?.renderWidgets) {
    if (Date.now() - waitingSince < KLAVIYO_WAIT_MS) scheduleRun(KLAVIYO_POLL_MS)
    else stopKlaviyoRender()
    return
  }

  klReviews.renderWidgets()

  if (retryIndex >= RENDER_RETRY_MS.length) {
    stopKlaviyoRender()
    return
  }

  scheduleRun(RENDER_RETRY_MS[retryIndex])
  retryIndex += 1
}

export class KlaviyoStarRatingElement extends HTMLElement {
  private observer: MutationObserver | null = null
  private widget: Element | null = null
  private hasNotifiedReady = false

  connectedCallback() {
    if (!this.querySelector(WIDGET_SELECTOR)) {
      this.renderFallbackMarkup()
    }

    this.widget = this.querySelector(WIDGET_SELECTOR)
    if (!this.widget) return

    this.observer = new MutationObserver(() => this.syncRenderedState())
    this.observer.observe(this.widget, { childList: true, subtree: true })

    if (this.syncRenderedState()) return

    requestKlaviyoRender(this)
  }

  disconnectedCallback() {
    pending.delete(this)
    this.stopObserving()
  }

  /** True once Klaviyo has rendered stars into the widget. */
  syncRenderedState(): boolean {
    if (!this.widget?.querySelector(RENDERED_SELECTOR)) return false

    this.setAttribute('data-ready', 'true')
    this.disableRatingButton()
    if (!this.hasAttribute(DISABLE_INTERACTION_ATTRIBUTE)) this.stopObserving()
    this.notifyReady()
    return true
  }

  /**
   * Klaviyo wraps the stars in a button that scrolls to (or opens) the reviews list, which
   * has nothing to jump to outside the PDP.
   */
  private disableRatingButton() {
    if (!this.hasAttribute(DISABLE_INTERACTION_ATTRIBUTE)) return

    this.widget
      ?.querySelectorAll<HTMLButtonElement>(RATING_BUTTON_SELECTOR)
      .forEach((button) => {
        button.disabled = true
        button.tabIndex = -1
      })
  }

  private renderFallbackMarkup() {
    const productId = this.getAttribute('product-id') || ''
    const productTitle = this.getAttribute('product-title') || ''
    const productType = this.getAttribute('product-type') || ''

    this.innerHTML = `
      <div class="klaviyo-star-rating-container flex flex-nowrap items-center">
        <div
          class="klaviyo-star-rating-widget flex"
          slot="productReviews"
          data-id="${productId}"
          data-product-title="${productTitle}"
          data-product-type="${productType}"
        ></div>
      </div>
    `
  }

  private notifyReady() {
    if (this.hasNotifiedReady) return
    this.hasNotifiedReady = true
    this.dispatchEvent(
      new CustomEvent(KLAVIYO_STAR_RATING_READY_EVENT, { bubbles: true })
    )
  }

  private stopObserving() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
